import { TOTAL_ROUNDS, ROUND_DISTANCES, HORSES_PER_RACE, RACE_STATUS } from '@/constants'
import { selectRandomItems, calculateRaceTime } from '@/utils/helpers'

const state = () => ({
  program: [],
  currentRound: 0,
  raceStatus: RACE_STATUS.IDLE,
  raceProgress: {},
  results: []
})

const getters = {
  raceProgram: state => state.program,
  
  currentRoundInfo: state => {
    if (!state.program.length || state.currentRound >= state.program.length) return null
    return state.program[state.currentRound]
  },
  
  currentRoundNumber: state => state.currentRound + 1,
  raceStatus: state => state.raceStatus,
  isRaceRunning: state => state.raceStatus === RACE_STATUS.RUNNING,
  isRacePaused: state => state.raceStatus === RACE_STATUS.PAUSED,
  isProgramReady: state => state.program.length > 0,
  raceProgress: state => state.raceProgress,
  allResults: state => state.results,
  isAllRoundsCompleted: state => state.currentRound >= TOTAL_ROUNDS && state.raceStatus === RACE_STATUS.FINISHED,
  getResultsByRound: state => idx => state.results.find(r => r.roundIndex === idx)
}

const mutations = {
  SET_PROGRAM(state, program) {
    state.program = program
    state.currentRound = 0
    state.raceStatus = RACE_STATUS.READY
    state.results = []
    state.raceProgress = {}
  },
  
  SET_RACE_STATUS(state, status) {
    state.raceStatus = status
  },
  
  INIT_RACE_PROGRESS(state) {
    const round = state.program[state.currentRound]
    if (!round) return
    
    const progress = {}
    round.horses.forEach(h => {
      progress[h.id] = { position: 0, finished: false, finishTime: null }
    })
    state.raceProgress = progress
  },
  
  UPDATE_HORSE_PROGRESS(state, { horseId, position, finished, finishTime }) {
    if (state.raceProgress[horseId]) {
      state.raceProgress[horseId] = { position, finished, finishTime }
    }
  },
  
  ADD_ROUND_RESULTS(state, results) {
    state.results.push(results)
  },
  
  NEXT_ROUND(state) {
    state.currentRound++
    state.raceStatus = state.currentRound >= TOTAL_ROUNDS ? RACE_STATUS.FINISHED : RACE_STATUS.READY
    state.raceProgress = {}
  },
  
  RESET_RACE(state) {
    state.program = []
    state.currentRound = 0
    state.raceStatus = RACE_STATUS.IDLE
    state.raceProgress = {}
    state.results = []
  }
}

const actions = {
  generateProgram({ commit, rootGetters }) {
    const horses = rootGetters['horses/allHorses']
    if (!horses.length) {
      console.error('No horses available')
      return
    }

    const program = []
    for (let i = 0; i < TOTAL_ROUNDS; i++) {
      const selected = selectRandomItems(horses, HORSES_PER_RACE)
      program.push({
        roundIndex: i,
        roundNumber: i + 1,
        distance: ROUND_DISTANCES[i],
        horses: selected.map((h, idx) => ({ ...h, lane: idx + 1 }))
      })
    }
    commit('SET_PROGRAM', program)
  },

  async startRace({ commit, state, dispatch }) {
    if (state.raceStatus === RACE_STATUS.RUNNING) return
    if (state.currentRound >= TOTAL_ROUNDS) return

    commit('SET_RACE_STATUS', RACE_STATUS.RUNNING)
    commit('INIT_RACE_PROGRESS')
    await dispatch('runRaceAnimation')
  },

  pauseRace({ commit, state }) {
    if (state.raceStatus === RACE_STATUS.RUNNING) {
      commit('SET_RACE_STATUS', RACE_STATUS.PAUSED)
    }
  },

  resumeRace({ commit, state, dispatch }) {
    if (state.raceStatus === RACE_STATUS.PAUSED) {
      commit('SET_RACE_STATUS', RACE_STATUS.RUNNING)
      dispatch('runRaceAnimation')
    }
  },

  toggleRace({ state, dispatch }) {
    if (state.raceStatus === RACE_STATUS.RUNNING) {
      dispatch('pauseRace')
    } else if (state.raceStatus === RACE_STATUS.PAUSED) {
      dispatch('resumeRace')
    } else if (state.raceStatus === RACE_STATUS.READY) {
      dispatch('startRace')
    }
  },

  runRaceAnimation({ commit, state }) {
    return new Promise(resolve => {
      const round = state.program[state.currentRound]
      if (!round) {
        resolve()
        return
      }

      const { distance, horses } = round
      const times = horses.map(h => ({
        id: h.id,
        raceTime: calculateRaceTime(h.condition, distance)
      }))

      const start = Date.now()

      const tick = () => {
        if (state.raceStatus !== RACE_STATUS.RUNNING) {
          resolve()
          return
        }

        const elapsed = Date.now() - start
        let done = true

        times.forEach(({ id, raceTime }) => {
          const pos = Math.min(100, (elapsed / raceTime) * 100)
          const fin = pos >= 100
          if (!fin) done = false

          commit('UPDATE_HORSE_PROGRESS', {
            horseId: id,
            position: pos,
            finished: fin,
            finishTime: fin ? raceTime : null
          })
        })

        if (done) {
          const sorted = times.sort((a, b) => a.raceTime - b.raceTime)
          const results = sorted.map((t, i) => {
            const horse = horses.find(h => h.id === t.id)
            return {
              position: i + 1,
              horseId: t.id,
              horseName: horse.name,
              horseColor: horse.color,
              time: t.raceTime
            }
          })

          commit('ADD_ROUND_RESULTS', {
            roundIndex: state.currentRound,
            roundNumber: state.currentRound + 1,
            distance,
            results
          })

          setTimeout(() => {
            commit('NEXT_ROUND')
            resolve()
          }, 1000)
        } else {
          requestAnimationFrame(tick)
        }
      }

      requestAnimationFrame(tick)
    })
  },

  resetRace({ commit }) {
    commit('RESET_RACE')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
